import React, { useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';

import { Task } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/types/task';

import SectionLoader from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import WorkspaceTaskEntry, {
  WorkspaceTaskEntryHeader,
} from '@/src/components/common/CustomCalendar/CalendarEntry/WorkspaceTaskEntry';

import NoTask from './NoTask';

interface WorkspaceAllViewProps {
  tasks: Task[];
  queryInfo: any;
  isReportsEnabled?: boolean;
}

const WorkspaceAllView: React.FC<WorkspaceAllViewProps> = ({
  tasks,
  queryInfo,
  isReportsEnabled,
}) => {
  const lastRowRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersect = useCallback(
    (entries) => {
      if (
        entries[0].isIntersecting &&
        queryInfo.hasNextPage &&
        !queryInfo.isFetchingNextPage &&
        !queryInfo.isRefetching
      ) {
        queryInfo.fetchNextPage();
      }
    },
    [queryInfo]
  );

  useEffect(() => {
    if (!lastRowRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(handleIntersect, { threshold: 0.7 });
    observerRef.current.observe(lastRowRef.current);
    return () => observerRef.current?.disconnect();
  }, [tasks, handleIntersect]);

  const { projectId } = useParams<{ projectId: string }>();

  return (
    <>
      {queryInfo.isLoading ? (
        <div className={clsx('tw-flex', 'tw-justify-center', 'tw-items-center', 'tw-h-40')}>
          <span className="spinner-border" role="status" aria-hidden="true" />
        </div>
      ) : tasks && tasks.length > 0 ? (
        <>
          {!isReportsEnabled && <WorkspaceTaskEntryHeader />}
          {tasks.map((task, idx) => {
            const isLast = idx === tasks.length - 1;
            return (
              <div key={task.taskId || idx} ref={isLast ? lastRowRef : undefined}>
                <WorkspaceTaskEntry task={task} compactWidth={isReportsEnabled} />
              </div>
            );
          })}
          {(queryInfo.isFetchingNextPage || queryInfo.isLoading) && (
            <SectionLoader className="tw-rounded-lg tw-bg-transparent" />
          )}
        </>
      ) : (
        <NoTask showProcessesLink projectId={projectId} />
      )}
    </>
  );
};
export default WorkspaceAllView;
